import { Component } from '@theme/component'

const pageCache = new Map()
let pageAbortController = null

function cacheCurrentPage(url) {
  if (url && !pageCache.has(url)) {
    pageCache.set(url, document.documentElement.outerHTML)
  }
}

function abortPendingPageFetch() {
  pageAbortController?.abort()
}

async function fetchPageDocument(url) {
  const cachedMarkup = pageCache.get(url)
  if (cachedMarkup) {
    return new DOMParser().parseFromString(cachedMarkup, 'text/html')
  }

  abortPendingPageFetch()
  pageAbortController = new AbortController()

  try {
    const response = await fetch(url, {
      credentials: 'same-origin',
      signal: pageAbortController.signal
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch page (${response.status})`)
    }
    const markup = await response.text()
    pageCache.set(url, markup)
    return new DOMParser().parseFromString(markup, 'text/html')
  } catch (error) {
    if (error.name === 'AbortError') {
      return null
    }
    throw error
  } finally {
    pageAbortController = null
  }
}

function swapMainContent(newDocument) {
  const currentMain = document.querySelector('main')
  const newMain = newDocument.querySelector('main')

  if (!(currentMain && newMain)) return false

  replaceMain(currentMain, newMain)
  window.dispatchEvent(new Event('page:changed'))
  return true
}

function replaceMain(currentMain, newMain) {
  currentMain.replaceWith(newMain)
}

if (!customElements.get('product-variations')) {
  class ProductVariationsComponent extends Component {
    connectedCallback() {
      super.connectedCallback()
      this.#cacheCurrentPage()
      window.addEventListener('popstate', this.#handlePopState)
    }

    disconnectedCallback() {
      super.disconnectedCallback()
      window.removeEventListener('popstate', this.#handlePopState)
      abortPendingPageFetch()
    }

    onProductClick(event) {
      const button = event.target.closest('[data-url]')
      if (!button) return

      event.preventDefault()

      const url = button.dataset.url
      if (!url) return
      const variantId = button.dataset.variantId
      const urlWithVariant = this.#appendVariantToUrl(url, variantId)

      this.#loadProduct(urlWithVariant, { sourceButton: button })
    }

    loadProduct(url, options) {
      return this.#loadProduct(url, options)
    }

    #handlePopState = () => {
      this.#loadProduct(window.location.href, {
        updateHistory: false
      })
    }

    async #loadProduct(
      url,
      { sourceButton = null, updateHistory = true } = {}
    ) {
      const targetUrl = this.#normalizeUrl(url)
      const currentUrl = this.#normalizeUrl(window.location.href)

      if (!targetUrl || (updateHistory && targetUrl === currentUrl)) return

      this.#setLoadingState(sourceButton, true)

      try {
        const newDocument = await fetchPageDocument(targetUrl)
        if (!newDocument) return

        const swapped = swapMainContent(newDocument)
        if (!swapped) {
          window.location.href = targetUrl
          return
        }

        this.#updateHeadMetadata(newDocument, targetUrl)

        if (updateHistory && targetUrl !== window.location.href) {
          history.pushState({}, '', targetUrl)
        }

        this.#scrollToTop()
      } catch (error) {
        window.location.href = targetUrl
      } finally {
        this.#setLoadingState(sourceButton, false)
      }
    }

    #updateHeadMetadata(newDocument, targetUrl) {
      const newTitle = newDocument.querySelector('title')?.textContent?.trim()
      if (newTitle) {
        document.title = newTitle
      }
      this.#syncMetaTag(newDocument, 'meta[property="og:title"]')
      this.#syncMetaTag(newDocument, 'meta[name="twitter:title"]')
      this.#syncMetaTag(newDocument, 'meta[name="description"]')
      this.#syncMetaTag(newDocument, 'meta[property="og:description"]')
      this.#syncMetaTag(newDocument, 'meta[property="og:image"]')
      this.#syncMetaTag(newDocument, 'link[rel="canonical"]', 'href', targetUrl)
      this.#syncMetaTag(
        newDocument,
        'meta[property="og:url"]',
        'content',
        targetUrl
      )
    }

    #cacheCurrentPage() {
      const url = this.#normalizeUrl(window.location.href)
      cacheCurrentPage(url)
    }

    #scrollToTop() {
      window.scrollTo({ behavior: 'smooth', top: 0 })
    }

    #setLoadingState(button, isLoading) {
      if (!(button instanceof HTMLElement)) return
      button.classList.toggle('is-loading', isLoading)
      button.toggleAttribute('disabled', isLoading)
      button.setAttribute('aria-busy', isLoading.toString())
    }

    #normalizeUrl(url) {
      try {
        return new URL(url, window.location.origin).toString()
      } catch {
        return null
      }
    }

    #appendVariantToUrl(url, variantId) {
      const normalizedUrl = this.#normalizeUrl(url)
      if (!normalizedUrl) {
        return url
      }

      if (!variantId) {
        return normalizedUrl
      }

      try {
        const nextUrl = new URL(normalizedUrl)
        nextUrl.searchParams.set('variant', String(variantId))
        return nextUrl.toString()
      } catch {
        return normalizedUrl
      }
    }

    #syncMetaTag(
      newDocument,
      selector,
      attribute = 'content',
      fallback = null
    ) {
      const newElement = newDocument.querySelector(selector)
      if (!newElement) return

      const newValue = newElement.getAttribute(attribute) ?? fallback
      const currentElement = document.querySelector(selector)

      if (!currentElement) return
      if (newValue == null) return

      currentElement.setAttribute(attribute, newValue)
    }
  }

  customElements.define('product-variations', ProductVariationsComponent)
}
